import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import EventCard from '../components/EventCard';
import supabase from '../lib/supabase';
import { useAuthStore } from '../store/authStore';

function Events() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [events, setEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'community' | 'info'>('community');

  useEffect(() => {
    const fetchEvents = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        setError(null);

        const { data: memberships } = await supabase
          .from('community_members')
          .select('community_id')
          .eq('user_id', user.id);

        const communityIds = memberships?.map(m => m.community_id) || [];

        if (communityIds.length > 0) {
          const { data: eventsData, error: eventsError } = await supabase
            .from('community_events')
            .select(`
              *,
              creator:creator_id(
                id,
                full_name,
                display_name,
                verified,
                avatar_url
              ),
              community:community_id(
                id,
                name
              ),
              rsvps:event_rsvps(count)
            `)
            .in('community_id', communityIds)
            .gte('start_time', new Date().toISOString())
            .order('start_time', { ascending: true });

          if (eventsError) throw eventsError;
          setEvents(eventsData || []);
        }
      } catch (err: any) {
        console.error('Error fetching events:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [user]);

  const handleEventUpdate = async () => {
    if (!user) return;

    try {
      const { data: memberships } = await supabase
        .from('community_members')
        .select('community_id')
        .eq('user_id', user.id);

      const communityIds = memberships?.map(m => m.community_id) || [];
      
      if (communityIds.length > 0) {
        const { data: eventsData, error: eventsError } = await supabase
          .from('community_events')
          .select(`
            *,
            creator:creator_id(
              id,
              full_name,
              display_name,
              verified,
              avatar_url
            ),
            community:community_id(
              id,
              name
            ),
            rsvps:event_rsvps(count)
          `)
          .in('community_id', communityIds)
          .gte('start_time', new Date().toISOString())
          .order('start_time', { ascending: true });

        if (eventsError) throw eventsError;
        setEvents(eventsData || []);
      }
    } catch (err) {
      console.error('Error updating events:', err);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-4">
        <h1 className="font-dillan text-4xl font-bold text-primary mb-4 text-center">
          Welcome to MusicNet
        </h1>
        <p className="font-dillan text-xl text-neutral max-w-2xl mx-auto text-center mb-8">
          Connect with music industry professionals, join communities, and discover events.
        </p>
        <button
          onClick={() => navigate('/auth')}
          className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
        >
          Get Started
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex flex-wrap justify-center sm:justify-between items-center gap-2 mb-6">
        <div className="flex flex-wrap justify-center bg-surface rounded-xl p-1 gap-2 sm:gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex-1 min-w-[90px] px-3 py-1 text-xs sm:text-sm rounded-full transition-colors font-dillan uppercase text-neutral border-4 border-secondary-light hover:text-primary"
          >
            Community Posts
          </button>
          
          <button
            onClick={() => {}}
            className="flex-1 min-w-[90px] px-3 py-1 text-xs sm:text-sm rounded-full transition-colors font-dillan uppercase text-neutral border-4 border-secondary-light hover:text-primary"
          >
            Events Feed
          </button>
          
          <button
            onClick={() => navigate('/', { state: { view: 'info' } })}
            className="flex-1 min-w-[90px] px-3 py-1 text-xs sm:text-sm rounded-full transition-colors font-dillan uppercase text-neutral border-4 border-secondary-light hover:text-primary"
          >
            Information Hub
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <Card>
            <div className="p-6 text-center">
              <p className="text-neutral">Loading events...</p>
            </div>
          </Card>
        ) : error ? (
          <Card>
            <div className="p-6 text-center">
              <p className="text-secondary-dark">{error}</p>
            </div>
          </Card>
        ) : events.length > 0 ? (
          events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onUpdate={handleEventUpdate}
            />
          ))
        ) : (
          <Card>
            <div className="p-6 text-center">
              <Calendar className="w-12 h-12 text-neutral mx-auto mb-4" />
              <p className="text-neutral">No upcoming events</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

export default Events;